import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase, Tables } from '../lib/supabase'
import { toast } from 'sonner'
import { useSessaoCapturaNotas, SELECT_NOTA, getGeo, attachSignedUrls } from './useSessaoCapturaNotas'

type SessaoCaptura = Pick<Tables<'sessoes_captura'>,
  'id' | 'uuid' | 'projeto_id' | 'contexto' | 'reuniao_id' | 'titulo' | 'iniciado_em' | 'encerrado_em' | 'latitude' | 'longitude'
>

interface NotaItem extends Tables<'notas'> {
  signedUrl?: string | null
  legenda_audio_path?: string | null
}

interface IniciarSessaoOpts {
  projeto_id?: number | null
  contexto?: string
  reuniao_id?: number | null
}

const STORAGE_KEY = 'sessao_ativa_uuid'
const BUCKET = 'notas'
const SIGNED_URL_REFRESH = 3600 * 1000 // refresh a cada 1h (ms)

export function useSessaoCaptura() {
  const [sessaoAtiva, setSessaoAtiva] = useState<SessaoCaptura | null>(null)
  const [items,       setItems]       = useState<NotaItem[]>([])
  const [loading,     setLoading]     = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items

  // ── Nota CRUD (delegado ao hook extraido) ──
  const {
    adicionarNota, adicionarNotaBackground,
    deletarNota, adicionarLegendaVoz, atualizarNota,
  } = useSessaoCapturaNotas({ sessaoAtiva, setItems, itemsRef })

  const _loadItems = useCallback(async (sessaoUuid: string) => {
    const { data } = await supabase
      .from('notas')
      .select(SELECT_NOTA)
      .eq('sessao_uuid', sessaoUuid)
      .order('criado_em', { ascending: true })
    if (data) setItems(await attachSignedUrls(data))
  }, [])

  // ── Recupera sessao ativa do localStorage (resiliencia a background) ──
  const recuperarSessao = useCallback(async () => {
    const uuid = localStorage.getItem(STORAGE_KEY)
    if (!uuid) return
    const { data } = await supabase
      .from('sessoes_captura')
      .select('id, uuid, projeto_id, contexto, reuniao_id, titulo, iniciado_em, encerrado_em, latitude, longitude')
      .eq('uuid', uuid)
      .is('encerrado_em', null)
      .single()
    if (data) {
      setSessaoAtiva(data)
      await _loadItems(uuid)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [_loadItems])

  useEffect(() => { recuperarSessao() }, [recuperarSessao])

  // ── Refresh automatico de signed URLs (sessoes longas) ──
  useEffect(() => {
    if (!sessaoAtiva) return
    const interval = setInterval(async () => {
      const withFiles = itemsRef.current.filter(n => n.storage_path)
      if (!withFiles.length) return
      const refreshed = await attachSignedUrls(itemsRef.current)
      setItems(refreshed)
    }, SIGNED_URL_REFRESH)
    return () => clearInterval(interval)
  }, [sessaoAtiva?.id])

  // ── Iniciar sessao ──
  const iniciarSessao = useCallback(async ({ projeto_id = null, contexto = 'LIVRE', reuniao_id = null }: IniciarSessaoOpts = {}) => {
    setLoading(true)
    try {
      const geo = await getGeo()
      const { data, error } = await supabase
        .from('sessoes_captura')
        .insert({ projeto_id, contexto, reuniao_id, ...(geo ?? {}) })
        .select('id, uuid, projeto_id, contexto, reuniao_id, titulo, iniciado_em, encerrado_em, latitude, longitude')
        .single()
      if (error) { toast.error('Erro ao iniciar sessão'); return null }
      localStorage.setItem(STORAGE_KEY, data.uuid)
      setSessaoAtiva(data)
      setItems([])
      return data
    } finally {
      setLoading(false)
    }
  }, [])

  // ── Encerrar sessao — retorna resumo ──
  const encerrarSessao = useCallback(async (titulo?: string) => {
    if (!sessaoAtiva) return null
    const { error } = await supabase
      .from('sessoes_captura')
      .update({ encerrado_em: new Date().toISOString(), ...(titulo ? { titulo } : {}) })
      .eq('id', sessaoAtiva.id)
    if (error) { toast.error('Erro ao encerrar sessão'); return null }

    const duracao = Math.floor((Date.now() - new Date(sessaoAtiva.iniciado_em).getTime()) / 1000)
    const resumo = {
      fotos:            items.filter(n => n.tipo === 'FOTO').length,
      audios:           items.filter(n => n.tipo === 'AUDIO').length,
      textos:           items.filter(n => n.tipo === 'TEXTO').length,
      desenhos:         items.filter(n => n.tipo === 'DESENHO').length,
      duracao_segundos: duracao,
    }
    localStorage.removeItem(STORAGE_KEY)
    setSessaoAtiva(null)
    setItems([])
    return resumo
  }, [sessaoAtiva, items])

  // ── Descartar sessao — apaga tudo ──
  const descartarSessao = useCallback(async () => {
    if (!sessaoAtiva) return
    const paths = items.filter(n => n.storage_path).map(n => n.storage_path)
    if (paths.length) await supabase.storage.from(BUCKET).remove(paths)
    await supabase.from('notas').delete().eq('sessao_uuid', sessaoAtiva.uuid)
    await supabase.from('sessoes_captura').delete().eq('id', sessaoAtiva.id)
    localStorage.removeItem(STORAGE_KEY)
    setSessaoAtiva(null)
    setItems([])
  }, [sessaoAtiva, items])

  // ── Deletar sessao JA encerrada (cascade) ──
  const deletarSessaoEncerrada = useCallback(async (sessaoId: number, sessaoUuid: string) => {
    const { data: notas } = await supabase
      .from('notas').select('id, storage_path, legenda_audio_path')
      .eq('sessao_uuid', sessaoUuid)
    const paths = (notas ?? [])
      .flatMap(n => [n.storage_path, n.legenda_audio_path])
      .filter(Boolean)
    if (paths.length) await supabase.storage.from(BUCKET).remove(paths)
    await supabase.from('notas').delete().eq('sessao_uuid', sessaoUuid)
    const { error } = await supabase.from('sessoes_captura').delete().eq('id', sessaoId)
    if (error) { toast.error('Erro ao excluir sessão'); return false }
    toast.success('Sessão excluída')
    return true
  }, [])

  // ── Atualizar campos de uma sessao (titulo, contexto) ──
  const updateSessao = useCallback(async (sessaoId: number, campos: Partial<Tables<'sessoes_captura'>>) => {
    const { error } = await supabase
      .from('sessoes_captura')
      .update(campos)
      .eq('id', sessaoId)
    if (error) { toast.error('Erro ao atualizar sessão'); return false }
    return true
  }, [])

  // ── Trocar projeto da sessao ativa ──
  const changeProjeto = useCallback(async (projetoId: number | null) => {
    if (!sessaoAtiva) return false
    const ok = await updateSessao(sessaoAtiva.id, { projeto_id: projetoId })
    if (ok) setSessaoAtiva(prev => prev ? { ...prev, projeto_id: projetoId } : prev)
    return ok
  }, [sessaoAtiva, updateSessao])

  return {
    sessaoAtiva, items, loading,
    iniciarSessao, encerrarSessao, descartarSessao, recuperarSessao,
    adicionarNota, adicionarNotaBackground, atualizarNota, adicionarLegendaVoz, deletarNota,
    deletarSessaoEncerrada, updateSessao, changeProjeto,
  }
}
